'use client';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PencilIcon, SidebarSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { MediaViewer, type MediaViewerType } from '@tranquility-ai/theme-components';
import { Pane } from '@tranquility-ai/theme-components/pane';
import '@tranquility-ai/theme-components/pane.css';
import '@tranquility-ai/theme-components/media-shell.css';
import '@tranquility-ai/theme-components/image-viewer.css';
import '@tranquility-ai/theme-components/media-toolbar.css';
import '@tranquility-ai/theme-components/player-controls.css';
import '@tranquility-ai/theme-components/pdf-viewer.css';
import '@tranquility-ai/theme-components/spreadsheet-viewer.css';
import '@tranquility-ai/theme-components/markdown-viewer.css';
import '@tranquility-ai/theme-components/plain-text-viewer.css';
import '@tranquility-ai/theme-components/json-viewer.css';
import '@tranquility-ai/theme-components/scrollbar.css';
import { MediaDisplay } from './ui/mediaDisplay';
import { buildMediaSourceFromUrl, formatTime, parseCitationPageFromMedia, recordStat } from '@/shared/functions';
import { rememberViewingPageForMedia, slideMediaInstanceScope } from '@/components/ui/pdfViewerPageHandoff';
import { hasTimelineDate, sortSlidesForHorizontalTimeline, formatSlideDateUtc } from '@/shared/clientFunctions';
import { isDisplayableMediaType } from '@/shared/constants';
import {
  cardControlledViewingPage,
  cardDocumentNeedsConversion,
  cardPageNumber,
  cardPdfViewerSrc,
  HORIZONTAL_TIMELINE_PAGEABLE_TYPES,
  rememberCardPage,
  resolveHorizontalTimelineCardPreview,
} from './horizontalTimelineCardMedia';
import { MediaSource, MediaType, Slide } from '@/shared/types';
import { StatsContext } from '../app/contexts';
import { stripMarkdownForSearch } from '@/shared/citationScrollNeedle';
import TPHorizontalTimelineEventModal from './tp-horizontal-timeline-event-modal';
import {
  SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS,
  SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS,
  SIDE_PANEL_BACKDROP_CLASS,
  SidePanelShell,
} from './side-panel';
import { TimelineEventsPanel, TIMELINE_EVENTS_PANEL_WIDTH_PX } from './timeline-events-panel';
import { useTimelineEvents } from './timeline-events-context';

interface TPHorizontalTimelineProps {
  title?: string;
  description?: string;
  media?: string;
  allSlides: Slide[];
  onOpenModal: (slide: Slide, column: string | null) => void;
  onOpenTitleSlideModal: (slide: Slide) => void;
  /** Persist a capture (or other media-context patch) onto this event. */
  onUpdateSlide?: (slide: Slide) => void;
  isEventModalOpen?: boolean;
  docIdMode?: boolean;
  featureFlags?: { [key: string]: any };
}

const COLUMN_WIDTH = 260;
/** Content width inside card padding (COLUMN_WIDTH − horizontal padding). */
const CARD_MEDIA_WIDTH = COLUMN_WIDTH - 32;
/** Letter-ish box so a converted Word/PowerPoint page fits the card, plus page nav. */
const CARD_DOCUMENT_HEIGHT = Math.round(CARD_MEDIA_WIDTH * 1.35);
const HTL_TIMELINE_PANE_CLASS = 'tp-htl-timeline-pane';
const HTL_INTERACTIVE_FOCUS_CLASS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--htl-marker)]';

const THEME_MEDIA_VIEWER_TYPES = new Set<MediaViewerType>([
  'image',
  'video',
  'audio',
  'pdf',
  'docx',
  'pptx',
  'spreadsheet',
  'text',
  'markdown',
  'html',
  'json',
  'website',
  'youtube',
]);

const toThemeMediaType = (mediaType?: MediaType | string): MediaViewerType | null => {
  if (!mediaType) return null;
  return THEME_MEDIA_VIEWER_TYPES.has(mediaType as MediaViewerType)
    ? (mediaType as MediaViewerType)
    : null;
};

const spreadsheetMiniLabel = (mediaItem: MediaSource, slide: Slide): string =>
  mediaItem.fileInfo?.fileName
  || mediaItem.truncatedFilePath
  || slide.slideTitle
  || 'Spreadsheet';

const toDescriptionPreview = (description: string): string =>
  stripMarkdownForSearch(description.replace(/<[^>]*>/g, '')).trim();

const resolveSlideMediaItem = (slide: Slide): MediaSource =>
  slide.media?.[0]
    ? (slide.media[0] as MediaSource)
    : ({
        mediaType: slide.mediaType,
        mediaSrc: slide.mediaSrc || slide.media?.[0]?.mediaSrc,
        mediaUrl: slide.mediaSrc || slide.media?.[0]?.mediaUrl,
        mediaContext: slide.media?.[0]?.mediaContext,
      } as MediaSource);

const getDisplayDate = (slide: Slide): string => {
  if (slide.id === 'TITLE_SLIDE') return 'Case Introduction';
  // Header rows use subtitle in place of date/time in presentation.
  if (!hasTimelineDate(slide) && slide.slideSubTitle && slide.slideSubTitle.trim() !== '') {
    return slide.slideSubTitle;
  }
  if (hasTimelineDate(slide)) {
    const formatted = formatSlideDateUtc(slide.date!, 'DD MMM YYYY');
    if (formatted) return formatted;
  }
  if (slide.displayDate && slide.displayDate.trim() !== '') return slide.displayDate;
  return 'Date not specified';
};

const TPHorizontalTimeline: React.FC<TPHorizontalTimelineProps> = ({
  title,
  description,
  media,
  allSlides,
  onOpenModal,
  onOpenTitleSlideModal,
  onUpdateSlide,
  isEventModalOpen = false,
}) => {
  const t = useTimePilotT();
  const statsContext = useContext(StatsContext);
  const timelineEvents = useTimelineEvents();

  const scrollRef = useRef<HTMLDivElement>(null);
  const handleDragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [scrollState, setScrollState] = useState<{ ratio: number; left: number }>({ ratio: 1, left: 0 });
  const [modalSlideId, setModalSlideId] = useState<string | undefined>();
  const [cardPages, setCardPages] = useState<Record<string, number>>({});
  const [panelMounted, setPanelMounted] = useState(false);

  const isEventsPanelOpen = timelineEvents?.isOpen ?? false;
  const closeEventsPanel = timelineEvents?.close;
  const closeEventsPanelRef = useRef(closeEventsPanel);
  closeEventsPanelRef.current = closeEventsPanel;

  useEffect(() => {
    setPanelMounted(true);
  }, []);

  useEffect(() => {
    if (!isEventsPanelOpen || !closeEventsPanel) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      event.preventDefault();
      closeEventsPanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isEventsPanelOpen, closeEventsPanel]);

  useEffect(() => () => {
    closeEventsPanelRef.current?.();
  }, []);

  // Build the ordered list of events, prepending a synthetic title slide when one is provided.
  // Include every visible slide — undated events stay on the axis after dated ones.
  const events = useMemo<Slide[]>(() => {
    const orderedSlides = [...allSlides].sort(sortSlidesForHorizontalTimeline);
    const mediaObject = media ? buildMediaSourceFromUrl(media) : null;
    const titleSlide: Slide | null = title || description || media ? {
      show: true,
      id: 'TITLE_SLIDE',
      slideTitle: title || 'Case Introduction',
      media: mediaObject ? [mediaObject] : [],
      mediaSrc: mediaObject?.mediaSrc || mediaObject?.mediaUrl,
      mediaType: mediaObject?.mediaType ?? ('Unknown' as MediaType),
      sources: [],
      slideDescription: description,
    } : null;
    return titleSlide ? [titleSlide, ...orderedSlides] : orderedSlides;
  }, [allSlides, title, description, media]);

  const modalSlide = modalSlideId
    ? events.find((event) => event.id === modalSlideId) ?? null
    : null;

  useEffect(() => {
    if (events.length === 0) {
      setSelectedId(undefined);
      return;
    }
    setSelectedId((prev) => (prev && events.some((e) => e.id === prev) ? prev : events[0].id));
  }, [events]);

  useEffect(() => {
    if (isEventModalOpen) {
      setModalSlideId(undefined);
    }
  }, [isEventModalOpen]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    const ratio = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
    const left = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 0;
    setScrollState({ ratio, left });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);
    window.addEventListener('resize', updateScrollState);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, events]);

  // Drag-to-scroll behaviour for the custom horizontal scroll handle.
  const onHandlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    handleDragRef.current = { startX: event.clientX, startScrollLeft: el.scrollLeft };
  }, []);

  const onHandlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const drag = handleDragRef.current;
    if (!el || !drag) return;
    const { scrollWidth, clientWidth } = el;
    const railWidth = (event.currentTarget.parentElement?.clientWidth ?? clientWidth) || 1;
    const deltaPx = event.clientX - drag.startX;
    el.scrollLeft = drag.startScrollLeft + (deltaPx / railWidth) * scrollWidth;
    updateScrollState();
  }, [updateScrollState]);

  const onHandlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    handleDragRef.current = null;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
  }, []);

  const handleSelect = useCallback((slide: Slide, event: React.MouseEvent | React.KeyboardEvent) => {
    setSelectedId(slide.id);
    if (!isEventModalOpen && slide.id) {
      setModalSlideId(slide.id);
    }
    recordStat('HorizontalTimelineEventClicked', event as any, statsContext);
  }, [isEventModalOpen, statsContext]);

  const handleJumpToEvent = useCallback((slide: Slide, event: React.MouseEvent | React.KeyboardEvent) => {
    setSelectedId(slide.id);
    if (slide.id) {
      const column = scrollRef.current?.querySelector(`[data-slide-id="${slide.id}"]`);
      column?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
    recordStat('HorizontalTimelineMenuItemClicked', event as any, statsContext);
    closeEventsPanel?.();
  }, [closeEventsPanel, statsContext]);

  const handleEdit = useCallback((slide: Slide, event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    if (slide.id === 'TITLE_SLIDE') {
      onOpenTitleSlideModal(slide);
    } else {
      onOpenModal(slide, null);
    }
    recordStat('HorizontalTimelineEditEventClicked', event as any, statsContext);
  }, [onOpenModal, onOpenTitleSlideModal, statsContext]);

  // Edit invoked from inside the expanded view modal: close the view, then open
  // the edit form so the two modals never stack on top of each other.
  const handleEditFromModal = useCallback((slide: Slide) => {
    setModalSlideId(undefined);
    if (slide.id === 'TITLE_SLIDE') {
      onOpenTitleSlideModal(slide);
    } else {
      onOpenModal(slide, null);
    }
  }, [onOpenModal, onOpenTitleSlideModal]);

  if (events.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <h1 className="text-4xl text-white">{t('timeline.noVisibleEvents')}</h1>
      </div>
    );
  }

  const renderDescriptionPreview = (description: string, clampClass = 'line-clamp-2') => {
    const preview = toDescriptionPreview(description);
    if (!preview) return null;
    return (
      <p className={`htl-event-description overflow-hidden break-words ${clampClass}`}>
        {preview}
      </p>
    );
  };

  const renderCardMedia = (slide: Slide) => {
    const mediaItem = resolveSlideMediaItem(slide);
    const mediaType = mediaItem.mediaType || slide.mediaType;
    const themeType = toThemeMediaType(mediaType);
    // The card paints the first clip. Later clips on the same slide keep
    // their own pages under the same helper with their own index.
    const cardScope = slideMediaInstanceScope(slide.id, 0);
    const chosenPage = cardControlledViewingPage(cardPages, cardScope);
    const cardPreview = resolveHorizontalTimelineCardPreview(mediaItem, mediaType, chosenPage);
    // Audio and video own the click (play/pause). Page controls are buttons
    // or the page-number field; the card ignores those, so paging does not
    // open the event. A click on the page itself still does.
    const stopCardClick = mediaType === 'audio' || mediaType === 'video';

    if (cardPreview.kind === 'capture') {
      return (
        <div className="htl-card-media w-full min-w-0 max-w-full overflow-hidden rounded-[4px]">
          <MediaViewer
            mode="mini"
            mediaType="image"
            src={cardPreview.src}
            alt={slide.slideTitle || spreadsheetMiniLabel(mediaItem, slide)}
            overlayChrome={false}
          />
        </div>
      );
    }

    if (!themeType) {
      return (
        <div className="htl-card-media w-full min-w-0 max-w-full overflow-hidden rounded-[4px]">
          <MediaDisplay
            mediaItem={mediaItem}
            isAccordion={false}
            isHorizontalTimeline
            containerWidth={CARD_MEDIA_WIDTH}
            showToolbarExpandButton={false}
          />
        </div>
      );
    }

    // Theme PdfViewer renders whatever URL it is given with pdf.js. A raw
    // Word or PowerPoint file is not a PDF. The event viewer converts those
    // through junkdrawer; the card has to use that same viewer.
    if (cardDocumentNeedsConversion(mediaItem, mediaType)) {
      const slideId = slide.id;
      // Only a page this card has already chosen. The citation fallback is
      // not a choice, and PdfViewer would persist it over the event's page.
      return (
        <div
          className="htl-card-media w-full min-w-0 max-w-full overflow-hidden rounded-[4px]"
          style={{ height: CARD_DOCUMENT_HEIGHT }}
        >
          <MediaDisplay
            key={slideId}
            mediaItem={mediaItem}
            isHorizontalTimeline
            containerWidth={CARD_MEDIA_WIDTH}
            containerHeight={CARD_DOCUMENT_HEIGHT}
            showToolbarExpandButton={false}
            viewingPageScope={cardScope}
            viewingPage={chosenPage}
            onViewingPageChange={
              cardScope
                ? (nextPage) => {
                    setCardPages((prev) => rememberCardPage(prev, cardScope, nextPage));
                    rememberViewingPageForMedia(mediaItem, nextPage, cardScope);
                  }
                : undefined
            }
          />
        </div>
      );
    }

    const miniLabel = spreadsheetMiniLabel(mediaItem, slide);
    const viewerType = themeType;
    const citationPage = parseCitationPageFromMedia(mediaItem);
    const pageable = HORIZONTAL_TIMELINE_PAGEABLE_TYPES.has(viewerType);
    const viewerSrc = pageable
      ? cardPdfViewerSrc(cardPreview.src, slide.id)
      : cardPreview.src;
    const page = pageable
      ? cardPageNumber(cardPages, cardScope, citationPage)
      : citationPage;

    if (!viewerSrc && viewerType !== 'spreadsheet' && !(viewerType === 'json' && mediaItem.content)) {
      return null;
    }

    return (
      <div
        className={`htl-card-media w-full min-w-0 max-w-full overflow-hidden rounded-[4px]${mediaType === 'audio' ? ' h-auto' : ''}`}
        {...(stopCardClick
          ? {
              onClick: (event: React.MouseEvent) => event.stopPropagation(),
              onKeyDown: (event: React.KeyboardEvent) => event.stopPropagation(),
            }
          : {})}
      >
        <MediaViewer
          key={slide.id ?? viewerSrc}
          mode="mini"
          mediaType={viewerType}
          src={viewerSrc}
          alt={slide.slideTitle || miniLabel}
          content={mediaItem.content}
          rotation={viewerType === 'image' ? cardPreview.rotation : undefined}
          page={page}
          onPageChange={
            pageable && cardScope
              ? (nextPage) => {
                  setCardPages((prev) => rememberCardPage(prev, cardScope, nextPage));
                  rememberViewingPageForMedia(mediaItem, nextPage, cardScope);
                }
              : undefined
          }
          overlayChrome={false}
          spreadsheet={
            themeType === 'spreadsheet'
              ? {
                  data: {
                    headers: [],
                    rows: [],
                    rowCount: 0,
                    offset: 0,
                    limit: 0,
                    sheetName: miniLabel,
                  },
                  rows: [],
                  miniLabel,
                }
              : undefined
          }
        />
      </div>
    );
  };

  const renderContent = (slide: Slide) => {
    const hasMedia = Boolean(
      slide.mediaType &&
      isDisplayableMediaType(slide.mediaType, { jsonMediaDisplay: true }) &&
      (slide.mediaSrc || slide.media?.[0] || (slide.mediaType === 'json' && slide.media?.[0]?.content)),
    );
    const isYoutube = slide.mediaType === 'youtube' && (slide.mediaSrc || slide.media?.[0]);
    const hasDescription = Boolean(slide.slideDescription?.trim());

    if (slide.id === 'TITLE_SLIDE') {
      return (
        <div className="flex min-w-0 flex-col gap-2.5 overflow-hidden">
          <p className="htl-event-headline line-clamp-3 break-words">
            {slide.slideTitle || 'Case Introduction'}
          </p>
          {hasMedia && renderCardMedia(slide)}
          {isYoutube && (
            <img
              data-testid="YoutubeItemButton"
              src={`https://img.youtube.com/vi/${slide.mediaSrc?.split('/').pop() || slide.media?.[0]?.mediaSrc?.split('/').pop()}/0.jpg`}
              alt={slide.slideTitle || 'Case Introduction'}
              className="w-full max-w-full rounded-[4px] object-cover"
            />
          )}
          {hasDescription && renderDescriptionPreview(slide.slideDescription as string, 'line-clamp-3')}
        </div>
      );
    }

    if (hasMedia) {
      const headline = slide.slideTitle || slide.slideSubTitle;
      return (
        <div className="flex min-w-0 flex-col gap-2.5 overflow-hidden">
          {headline && (
            <p className="htl-event-headline line-clamp-3 break-words">{headline}</p>
          )}
          {renderCardMedia(slide)}
          {hasDescription && renderDescriptionPreview(slide.slideDescription as string)}
        </div>
      );
    }

    if (isYoutube) {
      const headline = slide.slideTitle || slide.slideSubTitle;
      return (
        <div className="flex min-w-0 flex-col gap-2.5 overflow-hidden">
          {headline && (
            <p className="htl-event-headline line-clamp-3 break-words">{headline}</p>
          )}
          <img
            data-testid="YoutubeItemButton"
            src={`https://img.youtube.com/vi/${slide.mediaSrc?.split('/').pop() || slide.media?.[0]?.mediaSrc?.split('/').pop()}/0.jpg`}
            alt={headline || ''}
            className="w-full rounded-[4px] object-cover"
          />
          {hasDescription && renderDescriptionPreview(slide.slideDescription as string)}
        </div>
      );
    }

    if (hasDescription) {
      const headline = slide.slideTitle || slide.slideSubTitle;
      return (
        <div className="flex min-w-0 flex-col gap-2.5 overflow-hidden">
          {headline && (
            <p className="htl-event-headline line-clamp-3 break-words">{headline}</p>
          )}
          {renderDescriptionPreview(slide.slideDescription as string, 'line-clamp-[7]')}
        </div>
      );
    }

    return (
      <p className="htl-event-headline line-clamp-4 break-words">
        {slide.slideTitle || slide.slideSubTitle || 'Untitled event'}
      </p>
    );
  };

  const renderCard = (slide: Slide, isActive: boolean) => (
    <div className="htl-event-card-shell w-full min-w-0 max-w-full overflow-visible">
      <Pane
        title={<span className="truncate">{getDisplayDate(slide)}</span>}
        controls={
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            data-testid="HorizontalTimelineEditButton"
            aria-label={t('timeline.editEvent')}
            onClick={(event) => handleEdit(slide, event)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                handleEdit(slide, event);
              }
            }}
            className={HTL_INTERACTIVE_FOCUS_CLASS}
          >
            <PencilIcon weight="duotone" />
          </Button>
        }
        overflow="visible"
        className={`${HTL_TIMELINE_PANE_CLASS} w-full min-w-0 max-w-full ${isActive ? 'htl-card-active' : ''}`}
        bodyClassName="!px-4 !py-2.5 !min-w-0 overflow-hidden"
      >
        {renderContent(slide)}
      </Pane>
    </div>
  );

  const renderConnector = () => (
    <div className="flex w-full justify-center py-1">
      <div className="htl-connector h-6 w-[2px] opacity-20" />
    </div>
  );

  const renderDate = (slide: Slide) => {
    const showTime = slide.id !== 'TITLE_SLIDE' && slide.isTimeValid && slide.time;
    return (
      <div className="htl-axis-date flex flex-col items-center px-4 text-center">
        <span className="text-sm font-medium leading-5 text-white">{getDisplayDate(slide)}</span>
        {showTime && (
          <span className="text-xs font-medium leading-4 text-white/70">{formatTime(slide.time as string)}</span>
        )}
      </div>
    );
  };

  const renderMarker = (slide: Slide, isActive: boolean) => (
    <div className="flex h-4 w-full items-center justify-center" aria-hidden="true">
      <motion.span
        className="htl-marker-border block rounded-full border-2"
        animate={{
          width: isActive ? 16 : 12,
          height: isActive ? 16 : 12,
          backgroundColor: isActive ? 'var(--htl-marker)' : 'var(--htl-surface)',
        }}
        whileHover={{ scale: 1.2, backgroundColor: 'var(--htl-marker)' }}
        transition={{ duration: 0.15 }}
      />
    </div>
  );

  return (
    <>
    <div
      data-testid="horizontalTimelineView"
      className="htl-surface flex h-full w-full flex-col overflow-x-hidden overflow-y-visible rounded-[8px]"
    >
      <div className="relative min-h-0 flex-1 overflow-y-visible">
        {timelineEvents && (
          <div
            data-testid="HorizontalTimelineMenu"
            className="absolute left-4 top-4 z-20 sm:left-5 sm:top-5"
          >
            <Button
              type="button"
              iconOnly
              size="lg"
              style="solid"
              color={isEventsPanelOpen ? 'accent' : 'dark'}
              background="inverse"
              data-testid="HorizontalTimelineMenuToggle"
              aria-label={t('timeline.eventsPanel')}
              aria-pressed={isEventsPanelOpen}
              aria-expanded={isEventsPanelOpen}
              onClick={() => timelineEvents.toggle()}
              className={`shrink-0 ${HTL_INTERACTIVE_FOCUS_CLASS}`}
            >
              <SidebarSimpleIcon weight={isEventsPanelOpen ? 'fill' : 'regular'} />
            </Button>
          </div>
        )}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="h-full overflow-x-auto overflow-y-hidden scrollbar-hide overscroll-none"
        >
          <div className="htl-track relative inline-flex h-full min-w-full">
            {/* Axis */}
            <div className="htl-axis pointer-events-none absolute left-0 right-0 top-1/2 z-0 h-[2px] -translate-y-1/2" />
            {events.map((slide, index) => {
              const isActive = slide.id === selectedId;
              const isAbove = index % 2 === 0;
              return (
                <div
                  key={slide.id ?? index}
                  data-testid="HorizontalTimelineEvent"
                  data-slide-id={slide.id}
                  aria-label={`Select event: ${slide.slideTitle || slide.slideSubTitle || getDisplayDate(slide)}`}
                  tabIndex={0}
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('button, a, input, textarea, select, [role="button"]')) return;
                    handleSelect(slide, event);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    const target = event.target as HTMLElement;
                    if (target.closest('button, a, input, textarea, select, [role="button"]')) return;
                    event.preventDefault();
                    handleSelect(slide, event);
                  }}
                  className="htl-event-column group relative grid h-full min-w-0 shrink-0 cursor-pointer grid-rows-[1fr_auto_1fr] overflow-visible px-2 outline-none focus-visible:ring-2 focus-visible:ring-[var(--htl-marker)]"
                  style={{ width: COLUMN_WIDTH, maxWidth: COLUMN_WIDTH }}
                >
                  {/* Top zone */}
                  <div className="htl-event-zone flex min-h-0 min-w-0 max-w-full flex-col items-center justify-end overflow-visible">
                    {isAbove ? (
                      <>
                        {renderCard(slide, isActive)}
                        {renderConnector()}
                      </>
                    ) : (
                      renderDate(slide)
                    )}
                  </div>
                  {/* Axis marker */}
                  {renderMarker(slide, isActive)}
                  {/* Bottom zone */}
                  <div className="htl-event-zone flex min-h-0 min-w-0 max-w-full flex-col items-center justify-start overflow-visible">
                    {isAbove ? (
                      <div className="pt-1">{renderDate(slide)}</div>
                    ) : (
                      <>
                        {renderConnector()}
                        {renderCard(slide, isActive)}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Right-edge fade */}
        <div className="htl-fade pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-[42px]" />
      </div>

      {/* Custom horizontal scroll handle */}
      <div className="shrink-0 px-2 py-1">
        <div className="htl-rail relative h-2 w-full rounded-[4px]">
          <div
            data-testid="HorizontalTimelineScrollHandle"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            className="absolute top-0 h-2 cursor-grab rounded-[4px] htl-raised active:cursor-grabbing"
            style={{
              width: `${Math.max(8, scrollState.ratio * 100)}%`,
              left: `${scrollState.left * (100 - Math.max(8, scrollState.ratio * 100))}%`,
            }}
          />
        </div>
      </div>

      {modalSlide && !isEventModalOpen && (
        <TPHorizontalTimelineEventModal
          slide={modalSlide}
          onClose={() => setModalSlideId(undefined)}
          onEdit={handleEditFromModal}
          onUpdateSlide={onUpdateSlide}
          onViewingPageChange={(page, mediaIndex) => {
            const scope = slideMediaInstanceScope(modalSlide.id, mediaIndex);
            if (!scope) return;
            setCardPages((prev) => rememberCardPage(prev, scope, page));
          }}
        />
      )}
    </div>
    {panelMounted && timelineEvents && closeEventsPanel && typeof document !== 'undefined' &&
      createPortal(
        <AnimatePresence>
          {isEventsPanelOpen && (
            <>
              <motion.div
                key="timeline-events-backdrop"
                aria-hidden
                className={`${SIDE_PANEL_BACKDROP_CLASS} ${SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS} bg-black/20`}
                initial={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                animate={{ opacity: 1, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
                exit={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                onPointerDown={() => closeEventsPanel()}
              />
              <SidePanelShell
                key="timeline-events-panel"
                side="left"
                aria-modal={false}
                aria-label={t('timeline.eventsPanel')}
                className={`${SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS} max-w-[95vw]`}
                style={{ width: TIMELINE_EVENTS_PANEL_WIDTH_PX }}
              >
                <TimelineEventsPanel
                  events={events}
                  selectedId={selectedId}
                  onSelectEvent={handleJumpToEvent}
                  onClose={closeEventsPanel}
                />
              </SidePanelShell>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default React.memo(TPHorizontalTimeline);
